import { useTranslation } from 'react-i18next'
import { exerciseDataSchema } from '../types'
import { ExerciseMCQ } from './ExerciseMCQ'
import { ExerciseOpen } from './ExerciseOpen'
import { ExerciseCode } from './ExerciseCode'
import { ExerciseFillBlank } from './ExerciseFillBlank'

interface ExerciseBlockProps {
  raw: string
  /** Position dans le message assistant, dans l'ordre d'apparition — inscrit l'exercice à cet index dans la série (voir `ExerciseSetProvider`). */
  slotIndex: number
}

export function ExerciseBlock({ raw, slotIndex }: Readonly<ExerciseBlockProps>) {
  const { t } = useTranslation()

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return (
      <div className="my-3 rounded-xl border border-[var(--color-error)] bg-[var(--color-error-light)] px-4 py-3 text-sm text-[var(--color-error)]">
        {t('session.exercise_invalid_json')}
      </div>
    )
  }

  const result = exerciseDataSchema.safeParse(parsed)
  if (!result.success) {
    return (
      <div className="my-3 rounded-xl border border-[var(--color-error)] bg-[var(--color-error-light)] px-4 py-3 text-sm text-[var(--color-error)]">
        {t('session.exercise_unknown_format')}
      </div>
    )
  }

  const exercise = result.data

  const typeLabel = (() => {
    switch (exercise.type) {
      case 'mcq':
        return exercise.multiple
          ? t('session.exercise_type_mcq_multiple')
          : t('session.exercise_type_mcq_single')
      case 'open':
        return t('session.exercise_type_open')
      case 'code':
        return t('session.exercise_type_code')
      case 'fill':
        return t('session.exercise_type_fill')
    }
  })()

  return (
    <div className="my-3 rounded-xl border-2 border-[var(--color-primary-light)] bg-white overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[var(--color-primary-light)] to-transparent border-b border-[var(--color-primary-light)]">
        <span className="text-base" aria-hidden="true">✏️</span>
        <span className="text-xs font-bold uppercase tracking-wide text-[var(--color-primary)]">
          {t('session.exercise_label')}
        </span>
        <span className="ml-auto text-[10px] text-gray-400">
          {typeLabel}
          {exercise.type === 'code' && ` · ${exercise.language}`}
        </span>
      </div>

      <div className="p-4">
        {exercise.type === 'mcq' && <ExerciseMCQ exercise={exercise} slotIndex={slotIndex} />}
        {exercise.type === 'open' && <ExerciseOpen exercise={exercise} slotIndex={slotIndex} />}
        {exercise.type === 'code' && <ExerciseCode exercise={exercise} slotIndex={slotIndex} />}
        {exercise.type === 'fill' && <ExerciseFillBlank exercise={exercise} slotIndex={slotIndex} />}
      </div>
    </div>
  )
}
